/* =====================================================================
   REGISTA GLOBAL (EN) — HEADER & FOOTER REDESIGN
   ---------------------------------------------------------------------
   Vanilla, no dependencies. Every entry point checks for
   body.rg-redesign first, so removing that class disables this file
   along with the stylesheet.

   Four jobs:
     1. Mega-menu scroll indicator (1000–1399px)
     2. Footer link columns become an accordion below 1000px
     3. Drawer level 3: only one country open at a time
     4. Cart count badge hides itself when the cart is empty

   It never rebuilds the theme's navigation, search, localization form or
   WhatsApp button — it only reads scroll state and toggles attributes.
   ===================================================================== */

(function () {
  'use strict';

  var ROOT_CLASS = 'rg-redesign';
  var MOBILE = '(max-width: 999px)';

  function enabled() {
    return document.body && document.body.classList.contains(ROOT_CLASS);
  }

  /* ---------------------------------------------------------------
     1. Mega-menu scroll indicator
     The CSS paints the indicator as a single ::after on .mega-menu:
     a track in --rg-border with a thumb drawn as a background-image
     whose width and offset come from these two custom properties.
     In LTR scrollLeft starts at 0, so the thumb starts at the left.
     --------------------------------------------------------------- */

  function updateIndicator(track) {
    var panel = track.closest('.mega-menu');
    if (!panel) return;

    var visible = track.clientWidth;
    var total = track.scrollWidth;

    if (!total || total <= visible + 1) {
      // Nothing to scroll: fill the track so it reads as inert.
      panel.style.setProperty('--rg-scroll-thumb-width', '100%');
      panel.style.setProperty('--rg-scroll-thumb-pos', '0%');
      return;
    }

    var ratio = visible / total;
    // background-position is resolved against (container - image) width,
    // so the progress fraction maps straight onto 0–100%.
    var progress = track.scrollLeft / (total - visible);

    panel.style.setProperty('--rg-scroll-thumb-width', (ratio * 100).toFixed(3) + '%');
    panel.style.setProperty('--rg-scroll-thumb-pos', (Math.min(Math.max(progress, 0), 1) * 100).toFixed(3) + '%');
  }

  function initIndicators(scope) {
    var tracks = (scope || document).querySelectorAll('.mega-menu .mega-menu__linklist');

    Array.prototype.forEach.call(tracks, function (track) {
      if (track.dataset.rgIndicator === 'true') return;
      track.dataset.rgIndicator = 'true';

      track.addEventListener('scroll', function () {
        updateIndicator(track);
      }, { passive: true });

      // The panel is display:none until its <details> opens, so the first
      // measurement has to wait for that. ResizeObserver fires when the
      // track gains layout.
      if ('ResizeObserver' in window) {
        new ResizeObserver(function () {
          updateIndicator(track);
        }).observe(track);
      }

      var details = track.closest('details');
      if (details) {
        details.addEventListener('toggle', function () {
          if (details.open) updateIndicator(track);
        });
        // regista-v13-menu-fix.js opens on hover by setting the attribute
        // directly, which does not always fire 'toggle'.
        details.addEventListener('mouseenter', function () {
          window.requestAnimationFrame(function () {
            updateIndicator(track);
          });
        });
      }

      updateIndicator(track);
    });
  }

  /* ---------------------------------------------------------------
     2. Footer accordion (below 1000px)
     Marks up the three link columns with the attributes the stylesheet
     expects. No elements are created, moved or removed.
     --------------------------------------------------------------- */

  var accordionBlocks = [];

  function collectAccordionBlocks() {
    var footer = document.querySelector('.shopify-section--footer');
    if (!footer) return [];

    var found = [];

    // The two link blocks: heading is .h6, body is the sibling <ul>.
    Array.prototype.forEach.call(
      footer.querySelectorAll('.footer__block--links'),
      function (block) {
        var head = block.querySelector('.h6');
        var body = block.querySelector('ul');
        if (head && body) found.push({ block: block, head: head, body: body });
      }
    );

    // "Ways to reach us": heading is the contact title, body is the
    // channel list (WhatsApp + email buttons).
    var contact = footer.querySelector('.footer__block--payment_logo');
    if (contact) {
      var cHead = contact.querySelector('.regista-footer-contact__title');
      var cBody = contact.querySelector('.regista-footer-contact__channels');
      if (cHead && cBody) found.push({ block: contact, head: cHead, body: cBody });
    }

    return found;
  }

  function toggleBlock(entry) {
    var isOpen = entry.block.hasAttribute('data-rg-open');
    if (isOpen) {
      entry.block.removeAttribute('data-rg-open');
    } else {
      entry.block.setAttribute('data-rg-open', '');
    }
    entry.head.setAttribute('aria-expanded', String(!isOpen));
  }

  function enableAccordion() {
    accordionBlocks.forEach(function (entry, index) {
      entry.block.setAttribute('data-rg-acc', '');
      entry.head.setAttribute('data-rg-acc-head', '');
      entry.body.setAttribute('data-rg-acc-body', '');

      if (entry.head.dataset.rgBound !== 'true') {
        entry.head.dataset.rgBound = 'true';
        entry.head.setAttribute('role', 'button');
        entry.head.setAttribute('tabindex', '0');
        entry.head.addEventListener('click', function () {
          toggleBlock(entry);
        });
        entry.head.addEventListener('keydown', function (event) {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            toggleBlock(entry);
          }
        });
      }

      // Default state: the first row ("Information and service", which the
      // stylesheet orders first) is open, the other two closed.
      if (!entry.block.dataset.rgAccInit) {
        entry.block.dataset.rgAccInit = 'true';
        var openByDefault = entry.block.classList.contains('footer__block--links') && index === 1;
        if (openByDefault) {
          entry.block.setAttribute('data-rg-open', '');
        }
        entry.head.setAttribute('aria-expanded', String(openByDefault));
      }
    });
  }

  function disableAccordion() {
    accordionBlocks.forEach(function (entry) {
      entry.block.removeAttribute('data-rg-acc');
      entry.block.removeAttribute('data-rg-open');
      entry.head.removeAttribute('data-rg-acc-head');
      entry.head.removeAttribute('aria-expanded');
      entry.head.removeAttribute('role');
      entry.head.removeAttribute('tabindex');
      entry.body.removeAttribute('data-rg-acc-body');
      // Let the default open state be reapplied next time we go mobile.
      delete entry.block.dataset.rgAccInit;
    });
  }

  function syncAccordion() {
    if (!accordionBlocks.length) return;
    if (window.matchMedia(MOBILE).matches) {
      enableAccordion();
    } else {
      disableAccordion();
    }
  }

  /* ---------------------------------------------------------------
     2b. Tag the "All <country> teams" links
     The mega-menu and the drawer both give that row its own treatment
     (green, bold, trailing arrow). It cannot be found with :last-child,
     because a column that has no such row -- "Rest of the World" -- would
     get its last real team styled as if it were one. Match the label
     prefix instead and let CSS do the rest.
     --------------------------------------------------------------- */

  var ALL_PREFIX = /^all\b/i;

  function tagAllLinks(scope) {
    var links = (scope || document).querySelectorAll(
      ".mega-menu__linklist > li > ul a, .header-sidebar__nested-linklist > a"
    );

    Array.prototype.forEach.call(links, function (link) {
      var label = (link.textContent || "").trim();
      if (ALL_PREFIX.test(label)) {
        link.setAttribute("data-rg-all", "");
      } else {
        link.removeAttribute("data-rg-all");
      }
    });
  }

  /* ---------------------------------------------------------------
     3. Drawer level 3 — one country open at a time
     --------------------------------------------------------------- */

  function initDrawerAccordion(scope) {
    var panels = (scope || document).querySelectorAll('.header-sidebar__sub-panel');

    Array.prototype.forEach.call(panels, function (panel) {
      if (panel.dataset.rgExclusive === 'true') return;
      panel.dataset.rgExclusive = 'true';

      panel.addEventListener('toggle', function (event) {
        var opened = event.target;
        if (!opened || opened.tagName !== 'DETAILS' || !opened.open) return;

        Array.prototype.forEach.call(
          panel.querySelectorAll('details[open]'),
          function (other) {
            if (other !== opened) other.open = false;
          }
        );
      }, true); // 'toggle' does not bubble — capture instead.
    });
  }

  /* ---------------------------------------------------------------
     4. Cart count badge — hide at zero
     The theme's <cart-count> writes the raw number into the element and
     never hides itself, so a "0" would sit in the badge.
     --------------------------------------------------------------- */

  function syncCartCount() {
    Array.prototype.forEach.call(
      document.querySelectorAll('.header__secondary-nav cart-count'),
      function (node) {
        var value = parseInt((node.textContent || '').trim(), 10);
        node.classList.toggle('is-empty', !value);
      }
    );
  }

  function watchCartCount() {
    var nodes = document.querySelectorAll('.header__secondary-nav cart-count');
    if (!nodes.length) return;

    syncCartCount();

    if (!('MutationObserver' in window)) return;
    Array.prototype.forEach.call(nodes, function (node) {
      if (node.dataset.rgWatched === 'true') return;
      node.dataset.rgWatched = 'true';
      new MutationObserver(syncCartCount).observe(node, {
        childList: true,
        characterData: true,
        subtree: true
      });
    });
  }

  /* --------------------------------------------------------------- */

  function init() {
    if (!enabled()) return;

    initIndicators(document);
    initDrawerAccordion(document);
    tagAllLinks(document);

    accordionBlocks = collectAccordionBlocks();
    syncAccordion();

    watchCartCount();
  }

  function bind() {
    init();

    var mq = window.matchMedia(MOBILE);
    if (mq.addEventListener) {
      mq.addEventListener('change', syncAccordion);
    } else if (mq.addListener) {
      mq.addListener(syncAccordion);
    }

    window.addEventListener('resize', function () {
      if (!enabled()) return;
      Array.prototype.forEach.call(
        document.querySelectorAll('.mega-menu .mega-menu__linklist'),
        updateIndicator
      );
    }, { passive: true });

    // Theme editor: sections are re-rendered in place.
    document.addEventListener('shopify:section:load', function () {
      if (!enabled()) return;
      initIndicators(document);
      initDrawerAccordion(document);
      tagAllLinks(document);
      accordionBlocks = collectAccordionBlocks();
      syncAccordion();
      watchCartCount();
    });

    document.addEventListener('cart:change', syncCartCount);
    document.addEventListener('cart:refresh', syncCartCount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }

})();
